"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { NotificationBanner, type NotifBannerData } from "@/components/NotificationBanner";
import { useCustomer } from "@/context/CustomerContext";
import { applyAppBadge } from "@/lib/appBadge";
import {
  applyNotifLiveEvent,
  notifBadgeEnabledOnPath,
  unreadBadgeCount,
  type NotifBadgeRow,
} from "@/lib/notificationBadge";
import { shouldShowNotifBanner } from "@/lib/notificationBanner";
import {
  NOTIF_PREFS_EVENT,
  readNotifPrefs,
  type NotifPrefs,
} from "@/lib/notificationPrefs";
import { getNotifRoute } from "@/lib/notifRoute";
import { notifTopic } from "@/lib/realtimeTopics";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { subscribeLive } from "@/lib/supabase/liveChannel";
import { isPushEnabled } from "@/lib/supabase/push";
import { topLoader } from "@/lib/topLoader";

/** Status push perangkat dibaca ulang paling cepat sekali per jeda ini. */
const PUSH_RECHECK_MS = 60_000;

const NotificationContext = createContext<{ unread: number }>({ unread: 0 });

/**
 * Mesin badge lonceng notifikasi pembeli — SEKALI pasang di layout
 * (`Providers`), sementara loncengnya (`NotificationBell`) tampil di header
 * tiap halaman ber-keranjang. Header itu remount tiap navigasi; kalau mesin ini
 * ikut di sana, tiap pindah halaman = query 500 baris + join channel realtime
 * baru, dan badge ikon PWA berhenti diperbarui di halaman tanpa keranjang
 * (Pesanan/Tagihan/Akun/Jelajah). Di sini ia hidup sepanjang sesi, seperti
 * dulu saat lonceng masih di BottomNav.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const { customer } = useCustomer();
  const pathname = usePathname();
  const router = useRouter();
  const enabled = notifBadgeEnabledOnPath(pathname);
  const customerId = enabled ? (customer?.id ?? null) : null;
  const [rows, setRows] = useState<NotifBadgeRow[]>([]);
  const [prefs, setPrefs] = useState<NotifPrefs>({});
  // ─── Banner notifikasi masuk (lihat `shouldShowNotifBanner`) ────────────────
  const [banner, setBanner] = useState<NotifBannerData | null>(null);
  // Id yang sudah pernah terlihat sesi ini — "baru" = belum ada di sini.
  const seenRef = useRef<Set<string>>(new Set());
  // Nilai terkini untuk handler realtime (efeknya sengaja hanya bergantung
  // pada pembeli, bukan re-subscribe tiap pindah halaman / ganti preferensi).
  const pathnameRef = useRef(pathname);
  const prefsRef = useRef(prefs);
  const pushActiveRef = useRef(false);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);
  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  // Badge hanya menghitung jenis yang aktif (selaras Pengaturan Notifikasi).
  // Tanpa pembeli → 0: logout tak menyisakan angka basi di badge ikon PWA.
  const unread = customerId ? unreadBadgeCount(rows, prefs) : 0;

  // Ganti/keluar akun → buang baris milik pembeli sebelumnya sebelum fetch baru.
  useEffect(() => {
    setRows((prev) => (prev.length ? [] : prev));
    seenRef.current = new Set();
    setBanner(null);
  }, [customerId]);

  // Push perangkat aktif? Service worker menampilkan notif sistem untuk tiap
  // push — juga saat aplikasi terbuka — jadi di perangkat itu banner dilewati
  // (peringatan dobel). Dibaca saat pembeli berganti, saat keluar-masuk
  // halaman Notifikasi (tempat push dinyalakan/dimatikan), dan saat tab
  // kembali terlihat (dibatasi `PUSH_RECHECK_MS`).
  const onNotificationsPage = pathname === "/notifications";
  useEffect(() => {
    if (!customerId) {
      pushActiveRef.current = false;
      return;
    }
    let alive = true;
    let lastCheck = 0;
    const check = () => {
      lastCheck = Date.now();
      isPushEnabled("customer")
        .then((on) => {
          if (alive) pushActiveRef.current = on;
        })
        .catch(() => undefined);
    };
    check();
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastCheck > PUSH_RECHECK_MS) check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [customerId, onNotificationsPage]);

  // Angka belum-dibaca di ikon PWA terpasang (App Badging). Saat aplikasi
  // terbuka, ini menjaga badge tetap akurat (push SW menyetel saat tertutup).
  // Jalur admin/platform/toko tak menyentuh badge (dulu lonceng tak terpasang di sana).
  useEffect(() => {
    if (enabled) applyAppBadge(unread);
  }, [enabled, unread]);

  // Preferensi tampilan (localStorage). Re-baca saat berubah: event kustom utk
  // tab yang sama (diset di /notifications), `storage` utk lintas-tab.
  useEffect(() => {
    setPrefs(readNotifPrefs());
    const reload = () => setPrefs(readNotifPrefs());
    window.addEventListener(NOTIF_PREFS_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(NOTIF_PREFS_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);

  const muatBelumDibaca = useCallback(async () => {
    if (!customerId) return;
    const db = createBrowserSupabase();
    if (!db) return;
    // Limit + order SAMA dengan `NotificationsClient.refreshTotalUnread` (cermin
    // dua arah, dijaga `notificationBellMounted.test.ts`) — tanpa `order`, subset
    // yang kepotong tak deterministik antar fetch walau datanya tak berubah.
    const { data } = await db
      .from("notifications")
      .select("id, is_read, type, important")
      .eq("customer_id", customerId)
      .eq("is_read", false)
      .order("created_at", { ascending: false })
      .limit(500);
    for (const row of data ?? []) seenRef.current.add(row.id);
    setRows(data ?? []);
  }, [customerId]);

  useEffect(() => {
    void muatBelumDibaca();
  }, [muatBelumDibaca]);

  // Live: badge bergerak dari broadcast `notif:<customer_id>` (mig 4440).
  // Payload-nya sengaja memuat kolom badge (is_read/type/important) supaya
  // lonceng tak perlu round-trip untuk tiap notif — semuanya milik pembeli ini
  // sendiri, jadi tak ada yang bocor lewat topic yang memang cuma miliknya.
  useEffect(() => {
    if (!customerId) return;
    const db = createBrowserSupabase();
    if (!db) return;

    return subscribeLive(
      db,
      notifTopic(customerId),
      (event, payload) => {
        setRows((prev) => applyNotifLiveEvent(prev, event, payload));
        const id = payload.id;
        if (!id) return;
        const isNew = event === "INSERT" && !seenRef.current.has(id);
        seenRef.current.add(id);
        const show = shouldShowNotifBanner({
          event,
          isNew,
          type: payload.type,
          important: payload.important,
          prefs: prefsRef.current,
          pathname: pathnameRef.current,
          pushActive: pushActiveRef.current,
          visible: document.visibilityState === "visible",
        });
        if (!show) return;
        // Broadcast tak membawa judul/isi (payload sengaja minimal) → satu
        // query ber-indeks per notif BARU, RLS penuh, milik pembeli ini saja.
        void (async () => {
          const { data } = await db
            .from("notifications")
            .select("id, type, title, body, reference_id, link")
            .eq("id", id)
            .eq("customer_id", customerId)
            .maybeSingle();
          if (!data) return;
          setBanner({
            id: data.id,
            type: data.type,
            title: data.title,
            body: data.body,
            href: getNotifRoute(data.type, data.reference_id, data.link),
          });
        })();
      },
      { onDegraded: () => void muatBelumDibaca() }
    );
  }, [customerId, muatBelumDibaca]);

  const closeBanner = useCallback(() => setBanner(null), []);

  // Ketuk banner = ketuk baris di halaman Notifikasi: tandai dibaca, lalu buka
  // tujuannya (`getNotifRoute`, sumber yang sama dengan daftar).
  const openBanner = useCallback(
    (target: NotifBannerData) => {
      setBanner(null);
      if (customerId) {
        setRows((prev) => prev.filter((row) => row.id !== target.id));
        const db = createBrowserSupabase();
        if (db) {
          void (async () => {
            await db
              .from("notifications")
              .update({ is_read: true })
              .eq("id", target.id)
              .eq("customer_id", customerId);
          })();
        }
      }
      topLoader.startNavigation();
      router.push(target.href);
    },
    [customerId, router]
  );

  const value = useMemo(() => ({ unread }), [unread]);
  return (
    <NotificationContext.Provider value={value}>
      {children}
      {banner ? (
        <NotificationBanner banner={banner} onOpen={openBanner} onClose={closeBanner} />
      ) : null}
    </NotificationContext.Provider>
  );
}

/** Jumlah notifikasi belum-dibaca untuk badge lonceng — 0 untuk tamu / di luar provider. */
export function useUnreadNotifications(): number {
  return useContext(NotificationContext).unread;
}
